import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Modal from "../../ui/Modal";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Textarea from "../../ui/Textarea";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  asymmetricSplitLayoutStyle,
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  sectionHeaderStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
} from "../../ui/pagePatterns";
import {
  approveAuditPlan,
  createAuditPlan,
  createAuditPlanItem,
  createEngagementFromPlanItem,
  listAuditPlanItems,
  listAuditPlans,
  listAuditUniverse,
  updateAuditPlan,
} from "../../api/audit.api";

const defaultPlanForm = {
  name: "",
  description: "",
  plan_period_start: "",
  plan_period_end: "",
};

const defaultItemForm = {
  universe_item_id: "",
  planned_start_date: "",
  planned_end_date: "",
  priority_override: "",
  rationale: "",
};

const shellStyle = {
  ...pageShellStyle,
  gap: spacing.xl,
};

const embeddedShellStyle = {
  display: "grid",
  gap: spacing.lg,
};

const splitLayoutStyle = {
  ...asymmetricSplitLayoutStyle,
};

const listPaneStyle = {
  flex: "0 1 360px",
  minWidth: "min(320px, 100%)",
  maxWidth: 440,
};

const detailPaneStyle = {
  flex: "1 1 720px",
  minWidth: "min(360px, 100%)",
  display: "grid",
  gap: spacing.lg,
};

const surfaceHeaderStyle = {
  ...surfaceCardHeaderStyle,
  alignItems: "center",
};

const summaryBlockStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const eyebrowStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const summaryValueStyle = {
  fontWeight: typography.weight.bold,
  fontSize: typography.size.lg,
  color: colors.text,
};

const listBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.sm,
  maxHeight: "70vh",
  overflowY: "auto",
};

const planListButtonBaseStyle = {
  textAlign: "left",
  justifyContent: "flex-start",
  borderRadius: radius.md,
  padding: spacing.xl + spacing.xs,
  boxShadow: "none",
  display: "grid",
  gap: spacing.xs,
};

const planListMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const detailBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.lg,
};

const formGridStyle = {
  display: "grid",
  gap: spacing.md,
};

const planMetaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.md,
};

const formFieldStyle = {
  width: "100%",
  boxSizing: "border-box",
};

const sectionBlockStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  boxShadow: shadows.xs,
  padding: spacing.xl + spacing.xs,
  display: "grid",
  gap: spacing.md,
};

const sectionIntroStyle = {
  margin: 0,
  fontSize: typography.size.sm,
  color: colors.textMuted,
  lineHeight: 1.55,
};

const itemFormGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const itemCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.xl,
  background: colors.surface,
  display: "grid",
  gap: spacing.sm,
  boxShadow: shadows.xs,
};

const itemMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const footerActionRowStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const helperCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surface,
  padding: `${spacing.md + spacing.sm}px ${spacing.xl}px`,
  display: "grid",
  gap: spacing.xs / 2,
};

const helperLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const helperValueStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};

export default function AuditPlans({ embedded = false }) {
  const { id: routePlanId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [planForm, setPlanForm] = useState(defaultPlanForm);
  const [planEdit, setPlanEdit] = useState(defaultPlanForm);
  const [itemForm, setItemForm] = useState(defaultItemForm);

  const plansQuery = useQuery({
    queryKey: ["audit-plans"],
    queryFn: () => listAuditPlans({ limit: 100, offset: 0 }).then((res) => res.data),
  });

  const entitiesQuery = useQuery({
    queryKey: ["audit-universe", "for-plan-items"],
    queryFn: () => listAuditUniverse({ limit: 200, offset: 0, in_scope: true }).then((res) => res.data),
  });

  const selectedPlan = useMemo(
    () => plansQuery.data?.items?.find((plan) => plan.id === selectedPlanId) || null,
    [plansQuery.data, selectedPlanId]
  );

  const entityById = useMemo(() => {
    const mapping = {};
    (entitiesQuery.data?.items || []).forEach((entity) => {
      mapping[entity.id] = entity;
    });
    return mapping;
  }, [entitiesQuery.data?.items]);

  const planItemsQuery = useQuery({
    queryKey: ["audit-plan-items", selectedPlanId],
    queryFn: () => listAuditPlanItems(selectedPlanId, { limit: 200, offset: 0 }).then((res) => res.data),
    enabled: Boolean(selectedPlanId),
  });

  const createPlanMutation = useMutation({
    mutationFn: (payload) => createAuditPlan(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries(["audit-plans"]);
      setShowCreateModal(false);
      setPlanForm(defaultPlanForm);
      setSelectedPlanId(res.data?.id || res.id || null);
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, payload }) => updateAuditPlan(id, payload),
    onSuccess: () => queryClient.invalidateQueries(["audit-plans"]),
  });

  const approvePlanMutation = useMutation({
    mutationFn: (id) => approveAuditPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["audit-plans"]);
      queryClient.invalidateQueries(["audit-plan-items", selectedPlanId]);
    },
  });

  const addItemMutation = useMutation({
    mutationFn: (payload) => createAuditPlanItem(selectedPlanId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["audit-plan-items", selectedPlanId]);
      setItemForm(defaultItemForm);
    },
  });

  const createEngagementMutation = useMutation({
    mutationFn: ({ itemId, title }) =>
      createEngagementFromPlanItem(itemId, title ? { title } : {}).then((res) => res.data || res),
    onSuccess: (engagement) => {
      queryClient.invalidateQueries(["audit-plan-items", selectedPlanId]);
      navigate(`/audit/audits/${engagement.id}`);
    },
  });

  React.useEffect(() => {
    if (routePlanId) {
      setSelectedPlanId(Number(routePlanId));
      return;
    }
    if (!selectedPlan && plansQuery.data?.items?.length) {
      setSelectedPlanId(plansQuery.data.items[0].id);
    }
  }, [plansQuery.data, routePlanId, selectedPlan]);

  React.useEffect(() => {
    if (selectedPlan) {
      setPlanEdit({
        name: selectedPlan.name || "",
        description: selectedPlan.description || "",
        plan_period_start: selectedPlan.plan_period_start || "",
        plan_period_end: selectedPlan.plan_period_end || "",
      });
    }
  }, [selectedPlan]);

  const renderPlanList = () => {
    if (plansQuery.isError) {
      return (
        <PageState
          state="error"
          align="left"
          compact
          title="Audit plans are unavailable"
          description={`${plansQuery.error?.message || "Glyder could not load audit planning records."} Refresh to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={() => plansQuery.refetch()}>
              Refresh
            </Button>
          }
        />
      );
    }

    if (plansQuery.isLoading) {
      return (
        <PageState
          state="loading"
          align="left"
          compact
          title="Loading plans"
          description="Glyder is preparing the planning cycle and approved audit periods."
        />
      );
    }

    if (!(plansQuery.data?.items?.length > 0)) {
      return (
        <PageState
          state="empty"
          align="left"
          compact
          title="No audit plans yet"
          description="Create a planning period to define scope, schedule auditable entities, and prepare audits for execution."
          action={
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Plan
            </Button>
          }
        />
      );
    }

    return plansQuery.data.items.map((plan) => {
      const isActive = plan.id === selectedPlanId;
      return (
        <Button
          key={plan.id}
          variant="secondary"
          onClick={() => setSelectedPlanId(plan.id)}
          style={{
            ...planListButtonBaseStyle,
            border: `1px solid ${isActive ? colors.tableRowSelectedBorder : colors.border}`,
            background: isActive ? colors.tableRowSelected : colors.surface,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, flexWrap: "wrap" }}>
            <strong>{plan.name}</strong>
            <StatusBadge value={plan.status} fallbackLabel={plan.status || "Unknown"} />
          </div>
          <div style={planListMetaStyle}>
            {plan.plan_period_start || "Start date pending"} to {plan.plan_period_end || "End date pending"}
          </div>
          <div style={planListMetaStyle}>{plan.description || "No description provided yet."}</div>
        </Button>
      );
    });
  };

  const renderPlanItems = () => {
    if (!selectedPlan) {
      return (
        <PageState
          state="empty"
          align="left"
          title="Select a plan to continue"
          description="Choose an audit plan from the list to review metadata, schedule auditable entities, and create audits."
        />
      );
    }

    if (planItemsQuery.isError) {
      return (
        <PageState
          state="error"
          align="left"
          compact
          title="Plan items are unavailable"
          description={`${planItemsQuery.error?.message || "Glyder could not load the selected plan items."} Refresh the plan to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={() => planItemsQuery.refetch()}>
              Refresh
            </Button>
          }
        />
      );
    }

    if (planItemsQuery.isLoading) {
      return (
        <PageState
          state="loading"
          align="left"
          compact
          title="Loading plan items"
          description="Glyder is preparing scheduled auditable entities and linked audit creation actions."
        />
      );
    }

    if (!planItemsQuery.data?.items?.length) {
      return (
        <PageState
          state="empty"
          align="left"
          compact
          title="No items in this plan"
          description="Add auditable entities, schedule dates, and set priority to prepare audits for execution."
        />
      );
    }

    return (
      <div style={{ display: "grid", gap: spacing.sm }}>
        {planItemsQuery.data.items.map((item) => (
          <div key={item.id} style={itemCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, flexWrap: "wrap" }}>
              <strong>{entityById[item.universe_item_id]?.name || item.universe_item_id}</strong>
              <StatusBadge value={item.status} fallbackLabel={item.status || "Unknown"} />
            </div>
            <div style={itemMetaStyle}>
              {item.planned_start_date || "Start date pending"} to {item.planned_end_date || "End date pending"}
            </div>
            <div style={itemMetaStyle}>Priority: {item.priority_override || "-"}</div>
            {item.rationale ? <div style={sectionIntroStyle}>{item.rationale}</div> : null}
            <div style={footerActionRowStyle}>
              {!item.engagement_id ? (
                <Button
                  variant="primary"
                  disabled={selectedPlan.status !== "Approved" || createEngagementMutation.isLoading}
                  onClick={() => createEngagementMutation.mutate({ itemId: item.id })}
                >
                  Create Audit
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => navigate(`/audit/audits/${item.engagement_id}`)}>
                  Open Audit
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={embedded ? embeddedShellStyle : shellStyle}>
      {!embedded && <Breadcrumbs items={[{ label: "Audit Planning" }]} />}

      {!embedded && (
        <div style={pageHeaderStyle}>
          <div style={pageHeaderTitleBlockStyle}>
            <h1 style={pageTitleStyle}>Audit Planning</h1>
            <p style={pageSubtitleStyle}>
              Define planning periods, prioritize auditable entities, and create audits from approved plan items.
            </p>
          </div>
          <div style={pageHeaderActionsStyle}>
            <Button variant="secondary" onClick={() => plansQuery.refetch()}>
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Plan
            </Button>
          </div>
        </div>
      )}

      <div style={splitLayoutStyle}>
        <div style={{ ...surfaceCardStyle, ...listPaneStyle }}>
          <div style={surfaceHeaderStyle}>
            <div style={summaryBlockStyle}>
              <div style={eyebrowStyle}>Planning Cycles</div>
              <div style={summaryValueStyle}>{plansQuery.data?.total ?? 0} plans</div>
            </div>
            {embedded ? (
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                Create Plan
              </Button>
            ) : null}
          </div>
          <div style={listBodyStyle}>{renderPlanList()}</div>
        </div>

        <div style={detailPaneStyle}>
          <div style={surfaceCardStyle}>
            <div style={surfaceHeaderStyle}>
              <div style={summaryBlockStyle}>
                <div style={eyebrowStyle}>Plan Builder</div>
                <div style={summaryValueStyle}>{selectedPlan?.name || "Select a plan"}</div>
              </div>
              {selectedPlan ? (
                <div style={footerActionRowStyle}>
                  <StatusBadge value={selectedPlan.status} fallbackLabel={selectedPlan.status || "Unknown"} />
                  <Button
                    variant="secondary"
                    onClick={() => approvePlanMutation.mutate(selectedPlan.id)}
                    disabled={approvePlanMutation.isLoading || selectedPlan.status === "Approved"}
                  >
                    {selectedPlan.status === "Approved" ? "Approved" : "Approve Plan"}
                  </Button>
                </div>
              ) : null}
            </div>
            <div style={detailBodyStyle}>
              {!selectedPlan ? (
                <PageState
                  state="empty"
                  align="left"
                  title="No plan selected"
                  description="Choose a planning period from the left to review metadata, schedule auditable entities, and create audits."
                />
              ) : (
                <>
                  <div style={sectionBlockStyle}>
                    <div style={sectionHeaderStyle}>
                      <div style={summaryBlockStyle}>
                        <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Plan Metadata</div>
                        <p style={sectionIntroStyle}>
                          Capture the planning period, summary, and approval readiness before adding scheduled work.
                        </p>
                      </div>
                    </div>

                    <div style={planMetaGridStyle}>
                      <Input
                        label="Plan name"
                        value={planEdit.name}
                        onChange={(event) => setPlanEdit((prev) => ({ ...prev, name: event.target.value }))}
                        placeholder="Plan name"
                        style={formFieldStyle}
                        disabled={selectedPlan.status === "Approved"}
                      />
                      <Input
                        label="Period start"
                        type="date"
                        value={planEdit.plan_period_start ? planEdit.plan_period_start.slice(0, 10) : ""}
                        onChange={(event) => setPlanEdit((prev) => ({ ...prev, plan_period_start: event.target.value }))}
                        style={formFieldStyle}
                        disabled={selectedPlan.status === "Approved"}
                      />
                      <Input
                        label="Period end"
                        type="date"
                        value={planEdit.plan_period_end ? planEdit.plan_period_end.slice(0, 10) : ""}
                        onChange={(event) => setPlanEdit((prev) => ({ ...prev, plan_period_end: event.target.value }))}
                        style={formFieldStyle}
                        disabled={selectedPlan.status === "Approved"}
                      />
                    </div>
                    <Textarea
                      label="Description"
                      value={planEdit.description}
                      onChange={(event) => setPlanEdit((prev) => ({ ...prev, description: event.target.value }))}
                      placeholder="Description"
                      rows={3}
                      style={{ ...formFieldStyle, minHeight: 92 }}
                      disabled={selectedPlan.status === "Approved"}
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: spacing.sm }}>
                      <InfoCard label="Status" value={selectedPlan.status} />
                      <InfoCard label="Created by" value={selectedPlan.created_by || "-"} />
                      <InfoCard label="Approved by" value={selectedPlan.approved_by || "-"} />
                    </div>

                    {selectedPlan.status !== "Approved" ? (
                      <div style={footerActionRowStyle}>
                        <Button
                          variant="primary"
                          onClick={() => updatePlanMutation.mutate({ id: selectedPlan.id, payload: planEdit })}
                          disabled={updatePlanMutation.isLoading}
                        >
                          Save Plan Metadata
                        </Button>
                      </div>
                    ) : (
                      <PageState
                        state="empty"
                        compact
                        align="left"
                        title="Plan locked for execution"
                        description="Approved plans remain available for review and audit creation, but metadata is no longer editable."
                      />
                    )}
                  </div>

                  <div style={sectionBlockStyle}>
                    <div style={sectionHeaderStyle}>
                      <div style={summaryBlockStyle}>
                        <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Plan Items</div>
                        <p style={sectionIntroStyle}>
                          Select auditable entities, set schedule and priority, then create audits from approved plan items.
                        </p>
                      </div>
                    </div>

                    {selectedPlan.status !== "Approved" ? (
                      <div style={formGridStyle}>
                        {entitiesQuery.isError ? (
                          <PageState
                            state="error"
                            align="left"
                            compact
                            title="Auditable entities are unavailable"
                            description={`${entitiesQuery.error?.message || "Glyder could not load auditable entities for planning."} Refresh the workspace to try again.`}
                            action={
                              <Button type="button" variant="secondary" onClick={() => entitiesQuery.refetch()}>
                                Refresh
                              </Button>
                            }
                          />
                        ) : entitiesQuery.isLoading ? (
                          <PageState
                            state="loading"
                            align="left"
                            compact
                            title="Loading auditable entities"
                            description="Preparing entity options for this planning cycle."
                          />
                        ) : (
                          <form
                            onSubmit={(event) => {
                              event.preventDefault();
                              if (!itemForm.universe_item_id) return;
                              addItemMutation.mutate(itemForm);
                            }}
                            style={formGridStyle}
                          >
                            <div style={itemFormGridStyle}>
                              <Select
                                label="Auditable entity"
                                value={itemForm.universe_item_id}
                                onChange={(event) =>
                                  setItemForm((prev) => ({ ...prev, universe_item_id: event.target.value }))
                                }
                                style={formFieldStyle}
                                required
                              >
                                <option value="">Select an auditable entity</option>
                                {(entitiesQuery.data?.items || []).map((entity) => (
                                  <option key={entity.id} value={entity.id}>
                                    {entity.name}
                                  </option>
                                ))}
                              </Select>
                              <Input
                                label="Planned start"
                                type="date"
                                value={itemForm.planned_start_date}
                                onChange={(event) =>
                                  setItemForm((prev) => ({ ...prev, planned_start_date: event.target.value }))
                                }
                                style={formFieldStyle}
                              />
                              <Input
                                label="Planned end"
                                type="date"
                                value={itemForm.planned_end_date}
                                onChange={(event) =>
                                  setItemForm((prev) => ({ ...prev, planned_end_date: event.target.value }))
                                }
                                style={formFieldStyle}
                              />
                              <Input
                                label="Priority override"
                                value={itemForm.priority_override}
                                onChange={(event) =>
                                  setItemForm((prev) => ({ ...prev, priority_override: event.target.value }))
                                }
                                placeholder="Priority"
                                style={formFieldStyle}
                              />
                            </div>
                            <Textarea
                              label="Rationale"
                              value={itemForm.rationale}
                              onChange={(event) => setItemForm((prev) => ({ ...prev, rationale: event.target.value }))}
                              placeholder="Rationale"
                              rows={3}
                              style={{ ...formFieldStyle, minHeight: 92 }}
                            />
                            <div style={footerActionRowStyle}>
                              <Button type="submit" variant="primary" disabled={addItemMutation.isLoading}>
                                Add Plan Item
                              </Button>
                            </div>
                          </form>
                        )}
                      </div>
                    ) : null}

                    {renderPlanItems()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Audit Plan"
        description="Define the planning period and then add auditable entities to prepare audits for execution."
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!planForm.name || createPlanMutation.isLoading}
              onClick={() => createPlanMutation.mutate(planForm)}
            >
              Create Plan
            </Button>
          </>
        }
      >
        <div style={formGridStyle}>
          <Input
            label="Plan name"
            value={planForm.name}
            onChange={(event) => setPlanForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Plan name"
            style={formFieldStyle}
          />
          <Textarea
            label="Description"
            value={planForm.description}
            onChange={(event) => setPlanForm((prev) => ({ ...prev, description: event.target.value }))}
            placeholder="Description"
            rows={3}
            style={{ ...formFieldStyle, minHeight: 92 }}
          />
          <div style={planMetaGridStyle}>
            <Input
              label="Period start"
              type="date"
              value={planForm.plan_period_start}
              onChange={(event) => setPlanForm((prev) => ({ ...prev, plan_period_start: event.target.value }))}
              style={formFieldStyle}
            />
            <Input
              label="Period end"
              type="date"
              value={planForm.plan_period_end}
              onChange={(event) => setPlanForm((prev) => ({ ...prev, plan_period_end: event.target.value }))}
              style={formFieldStyle}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div style={helperCardStyle}>
      <div style={helperLabelStyle}>{label}</div>
      <div style={helperValueStyle}>{value || "-"}</div>
    </div>
  );
}
